import { useQuery } from '@tanstack/react-query';
import { apiService, type SubscriptionDto } from '../services/api';
import { queryKeys } from '../lib/queryKeys';

/**
 * Returns the caller's current subscription row. Refreshes every 30 s while
 * the tab is visible so the trial-remaining countdown on the Dashboard banner
 * stays accurate after webhook state transitions.
 *
 * Returns `null` when the business has no subscription yet (HTTP 404 from
 * /subscription/current is mapped to null inside apiService.getCurrentSubscription).
 */
export function useCurrentSubscription() {
    return useQuery<SubscriptionDto | null>({
        queryKey: queryKeys.subscription.current(),
        queryFn: () => apiService.getCurrentSubscription(),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
    });
}

/**
 * Maps the backend enum string to the numeric SubscriptionStatus expected by
 * TrialBanner. Stays forgiving — unknown values return 0 (banner won't render).
 */
export function subscriptionStatusToNumber(status: string | number | undefined): number {
    if (typeof status === 'number') return status;
    if (!status) return 0;
    switch (status.trim().toLowerCase()) {
        case 'active': return 1;
        case 'expired': return 2;
        case 'cancelled':
        case 'canceled': return 3;
        case 'pastdue':
        case 'past_due': return 4;
        case 'trial':
        case 'trialing': return 5;
        case 'incomplete': return 6;
        default: return 0;
    }
}
